<section>
	<text-group>
		<wrapper class="text-bold-3">
			<div>{{ 'ledger.summary.plan-value' | translate }}</div>
			<div [loading]="loading" class="align-right">{{ totalAccountingPlanValue | currency }}</div>
		</wrapper>

		@if (editMode || totalLedgerValue != totalAccountingPlanValue) {
		<wrapper class="text-bold-3" [ngClass]="{ 'status-color-positive': totalLedgerValue === totalAccountingPlanValue, 'status-color-negative': totalLedgerValue !== totalAccountingPlanValue }">
			<div>{{ 'ledger.summary.ledger-value' | translate }}</div>
			<div [loading]="loading" class="align-right">{{ totalLedgerValue | currency }}</div>
		</wrapper>
		}
		@if (editMode || totalLedgerValue != totalAccountingPlanValue) {
		<wrapper class="text-bold-3" [ngClass]="{ 'status-color-positive': totalLedgerValue === totalAccountingPlanValue, 'status-color-negative': totalLedgerValue !== totalAccountingPlanValue }">
			<div>{{ 'ledger.summary.plan-value-ledger-value-difference' | translate }}</div>
			<div [loading]="loading" class="align-right">{{ totalLedgerValue - totalAccountingPlanValue | currency }}</div>
		</wrapper>
		}

		<div>{{ 'ledger.summary.unposted-invoice-value' | translate }}</div>
		<div [loading]="loading" class="align-right">{{ unpostedInvoiceValue | currency }}</div>

		<div>{{ 'ledger.summary.invoiced-net-value' | translate }}</div>
		<div [loading]="loading" class="align-right">{{ postedInvoiceValue + creditedValue | currency }}</div>
	</text-group>

	<text-group>
		<wrapper class="text-bold-3">
			<div>{{ 'ledger.summary.posted-invoice-value' | translate }}</div>
			<div [loading]="loading" class="align-right">{{ postedInvoiceValue | currency }}</div>
		</wrapper>

		<div>{{ 'ledger.summary.open-invoice-value' | translate }}</div>
		<div [loading]="loading" class="align-right">{{ postedInvoiceValue - closedInvoiceValue | currency }}</div>

		<div>{{ 'ledger.summary.closed-value' | translate }}</div>
		<div [loading]="loading" class="align-right">{{ closedValue | currency }}</div>
	</text-group>

	<text-group>
		<wrapper class="text-bold-3">
			<div>{{ 'ledger.summary.credited-value' | translate }}</div>
			<div [loading]="loading" class="align-right">{{ creditedValue | currency }}</div>
		</wrapper>
	</text-group>

</section>

<section>
	@if (!showClosedPosts) {
	<a (click)="showClosedPosts = true">{{ 'ledger-posts.reveal-posted-ledger-posts' | translate }}</a>
	}
</section>

@if (loading) {
	<skeletons [defaultSkeletons]="3" />
}

<list-view scrollable [loading]="loading" [selectable]="false">
	<header defaultSort="ascending">{{ 'ledger-posts.column.planned-date' | translate }}</header>
	@if (displayProductName) {
		<header>{{ 'company.product-plans.product-name' | translate }}</header>
	}
	<header>{{ 'ledger-posts.column.type' | translate }}</header>
	<header>{{ 'ledger-posts.column.postedDate' | translate }}</header>
	<header>{{ 'ledger-posts.column.overdue' | translate }}</header>
	<header>{{ 'ledger-posts.column.open' | translate }}</header>
	<header align-right>{{ 'ledger-posts.column.value' | translate }}</header>

	<row [groupBy]="splitByKey ? post[splitByKey] : undefined" *listData="let post from posts; let listData=listData" [attr.post-id]="post.id" [disabled]="isDisabled(post)" [attr.accounting-plan-id]="post.accountingPlanId" [clickable]="false">
		@if (!editMode || !isEditable(post)) {
			<column [sortValue]="post.plannedDate">{{ post.plannedDate | customDate: 'shortDate' }}</column>
		}
		@if (editMode && isEditable(post)) {
			<column [sortValue]="post.plannedDate">
				<date-input [(value)]="post.plannedDate" [minDate]="today" [maxDate]="accountingPlan && accountingPlan.endDate && accountingPlan.startDate && accountingPlan.endDate > accountingPlan.startDate && accountingPlan.endDate >= today ? accountingPlan.endDate : undefined" [required]="true"></date-input>
			</column>
		}
		@if (displayProductName) {
			<column>{{ productPlanMap.get(post.accountingPlan.productPlanId) | productName }}</column>
		}
		<column>
			@if (!!post.ledgerHeader?.postedDate && !editMode && !editMigratedData) {
				<a (click)="navigateToInvoice(post)">{{ ('enum.ledger-post-type.' + ledgerPostType[post.type]) | translate }}</a>
			}
			@else {
				{{ ('enum.ledger-post-type.' + ledgerPostType[post.type]) | translate }}
			}
		</column>
		@if (post.ledgerHeader?.postedDate; as postedDate) {
			<column [sortValue]="postedDate">{{ postedDate | customDate: 'shortDate' }}</column>
		}
		@else {
			<column>{{ ((post.type !== ledgerPostType.MigratedInvoice && post.type !== ledgerPostType.CounterPost && post.type !== ledgerPostType.EqualizingCounterPost) ? 'general.false' : 'ledger-posts.not-available') | translate }}</column>
		}
		<column>{{ (post.ledgerHeader?.postedDate ? isOverdue(post) ? 'general.true' : 'general.false' : 'ledger-posts.not-available') | translate }}</column>
		<column>{{ (isClosed(post) ? 'general.false' : (isOpen(post) ? 'general.true' : 'ledger-posts.not-available')) | translate }}</column>
		@if (!isEditable(post)) {
			<column align-right [sortValue]="post.value">{{ getValue(post) | currency }}</column>
		}
		@else {
			<column align-right [sortValue]="post.value">
				<custom-input [inputType]="inputType.Currency" [(value)]="post.value" [required]="true" textAlign="right" (valueChange)="calculateLedgerValues()" />
			</column>
		}
		@if (editMode && isEditable(post)) {
			<menu>
				<menuitem (click)="copyDown(post, listData.sortedObjects)" icon="arrow-alt-from-top">{{ 'ledger-posts.copy-value-downwards' | translate }}</menuitem>
				<menuitem (click)="removePost(post)" icon="trash">{{ 'ledger-posts.remove-post' | translate }}</menuitem>
			</menu>
		}
	</row>
</list-view>

@if (editMode && !splitByKey) {
	<section>
		<button [loader]="false" (click)="addPost()" [ignoreValidation]="true">{{ 'ledger.edit-ledger-posts.add-new' | translate }}</button>
	</section>
}
